/**
 * Settings Layout Route
 *
 * Layout wrapper for all settings pages with sidebar navigation.
 * Uses useRouterState for reactive pathname (not window.location).
 */

import {
  Image,
  Inline,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  Text,
  UserMenu,
} from '@cortex/ui';
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { ArrowLeft, Bell, Key, User } from 'lucide-react';

import { supabase } from '@/lib/supabase';
import { useAuthContext } from '@/providers/auth-provider';

export const Route = createFileRoute('/_app/settings')({
  component: SettingsLayout,
});

function SettingsLayout() {
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isAdmin = user?.role === 'admin';

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate({ to: '/login' });
  };

  return (
    <SidebarProvider className="bg-background h-screen">
      <Sidebar>
        <SidebarHeader>
          <Inline align="center" justify="between">
            <Link to="/" className="flex items-center px-[7%]">
              <Image
                src="/cortex-logo.webp"
                alt="Cortex"
                className="h-auto w-[84%]"
              />
            </Link>
            <SidebarTrigger />
          </Inline>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={false}
                    tooltip="Back to Dashboard"
                    onClick={() => navigate({ to: '/' })}
                  >
                    <ArrowLeft
                      className="h-5 w-5 shrink-0"
                      aria-hidden="true"
                    />
                    <Text as="span" truncate className="flex-1 text-left">
                      Back to Dashboard
                    </Text>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupLabel>Settings</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={
                      pathname.includes('/settings/profile') ||
                      pathname === '/settings'
                    }
                    tooltip="Profile"
                    onClick={() => navigate({ to: '/settings/profile' })}
                  >
                    <User className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <Text as="span" truncate className="flex-1 text-left">
                      Profile
                    </Text>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={pathname.includes('/settings/notifications')}
                    tooltip="Notifications"
                    onClick={() => navigate({ to: '/settings/notifications' })}
                  >
                    <Bell className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <Text as="span" truncate className="flex-1 text-left">
                      Notifications
                    </Text>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={pathname.includes('/settings/api-keys')}
                    tooltip="API Keys"
                    onClick={() => navigate({ to: '/settings/api-keys' })}
                  >
                    <Key className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <Text as="span" truncate className="flex-1 text-left">
                      API Keys
                    </Text>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          {user ? (
            <UserMenu
              user={{
                email: user.email,
                ...(user.name != null && { name: user.name }),
                ...(user.avatarUrl != null && { avatarUrl: user.avatarUrl }),
                ...(user.role != null && { role: user.role }),
                ...(user.organization != null && {
                  organization: user.organization,
                }),
              }}
              collapsed={false}
              {...(isAdmin && {
                onAdminClick: () => navigate({ to: '/admin' }),
              })}
              onSettingsClick={() => navigate({ to: '/settings' })}
              onLogoutClick={handleLogout}
              side="top"
              align="start"
            />
          ) : null}
        </SidebarFooter>
      </Sidebar>

      <SidebarInset className="p-6">
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  );
}
